// src/components/technician/TechnicianTicketDetail.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Dialog, AppBar, Toolbar, IconButton, Typography, Box, Button,
  Container, Card, CardContent, Stack, TextField, FormControl,
  Select, MenuItem, CircularProgress, Snackbar, Alert, Chip,
  Divider, Avatar, Slide, InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon            from "@mui/icons-material/Close";
import EditNoteIcon         from "@mui/icons-material/EditNote";
import InfoOutlinedIcon     from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon     from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon      from "@mui/icons-material/CheckCircle";
import InventoryIcon        from "@mui/icons-material/Inventory2";
import PersonIcon           from "@mui/icons-material/Person";
import VerifiedUserIcon     from "@mui/icons-material/VerifiedUser";
import UploadFileIcon       from "@mui/icons-material/UploadFile";
import ReceiptLongIcon      from "@mui/icons-material/ReceiptLong";
import DeleteIcon           from "@mui/icons-material/Delete";

import { useAuth } from "../../contexts/AuthContext";
import { helpDeskService } from "../../services/helpDeskService";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = ["Open", "In Progress", "Resolved", "Closed"];

const STATUS_COLORS = {
  Open:          { bg: "#fee2e2", fg: "#991b1b" },
  "In Progress": { bg: "#dbeafe", fg: "#1e40af" },
  Resolved:      { bg: "#dcfce7", fg: "#166534" },
  Closed:        { bg: "#f3f4f6", fg: "#374151" },
};

const PRIORITY_COLORS = {
  Low:      { bg: "#e8f5e9", fg: "#2e7d32" },
  Medium:   { bg: "#e3f2fd", fg: "#1565c0" },
  High:     { bg: "#fff3e0", fg: "#ef6c00" },
  Critical: { bg: "#ffebee", fg: "#c62828" },
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const formatDate = (val) => {
  if (!val) return "—";
  const d = typeof val?.toDate === "function"
    ? val.toDate()
    : val?.seconds !== undefined
    ? new Date(val.seconds * 1000)
    : new Date(val);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const getWarrantyStatus = (expiry) => {
  if (!expiry) return { label: "N/A", bg: "#f3f4f6", fg: "#6b7280" };
  const exp = new Date(expiry);
  if (isNaN(exp)) return { label: "N/A", bg: "#f3f4f6", fg: "#6b7280" };
  const days = Math.ceil((exp - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0)   return { label: "Expired",         bg: "#fee2e2", fg: "#991b1b" };
  if (days <= 30) return { label: `${days}d left`,   bg: "#fef3c7", fg: "#92400e" };
  return               { label: "Active",            bg: "#dcfce7", fg: "#166534" };
};

// ─────────────────────────────────────────────────────────────────────────────
// SLIDE TRANSITION
// ─────────────────────────────────────────────────────────────────────────────

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─────────────────────────────────────────────────────────────────────────────
// STYLED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: "1px solid #e2e8f0",
}));

const SectionTitle = styled(Typography)(() => ({
  fontSize: "0.9375rem",
  fontWeight: 700,
  color: "#1e293b",
  marginBottom: "1.25rem",
  paddingBottom: "0.75rem",
  borderBottom: "1px solid #e5e7eb",
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.025em",
  marginBottom: "0.25rem",
}));

const FieldValue = styled(Typography)(() => ({
  fontSize: "0.9375rem",
  fontWeight: 600,
  color: "#1f2937",
}));

const FormRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "1rem",
  marginBottom: "1rem",
  "&:last-child": { marginBottom: 0 },
  [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
}));

const FormGroup = styled(Box, {
  shouldForwardProp: (p) => p !== "hasError" && p !== "layoutType",
})(({ hasError, layoutType }) => ({
  gridColumn: layoutType === "full" ? "1 / -1" : layoutType === "compact" ? "span 1" : "span 2",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "0.375rem",
  "& .MuiOutlinedInput-root": {
    background: "#fff",
    "& fieldset": { borderColor: hasError ? "#dc2626" : "#d1d5db" },
    "&:hover fieldset": { borderColor: hasError ? "#dc2626" : "#3b82f6" },
    "&.Mui-focused fieldset": {
      borderColor: hasError ? "#dc2626" : "#3b82f6",
      boxShadow: hasError ? "0 0 0 3px rgba(220,38,38,0.12)" : "0 0 0 3px rgba(59,130,246,0.12)",
    },
  },
}));
const InfoTile = styled(Box)(() => ({
  flex: 1,
  minWidth: 200,
  padding: "1rem",
  borderRadius: 8,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
}));

// ─── Button sx ──────────────────────────────────────────────────────────────

const cancelBtnSx = {
  px: 3, py: 1, backgroundColor: "#f3f4f6", color: "#374151",
  border: "1px solid #d1d5db", borderRadius: "6px", fontWeight: 500,
  textTransform: "none", minWidth: 100, height: 40,
  "&:hover":    { backgroundColor: "#e5e7eb" },
  "&:disabled": { opacity: 0.6 },
};

const submitBtnSx = {
  px: 3, py: 1, backgroundColor: "#2563eb", color: "#fff",
  borderRadius: "6px", fontWeight: 600, textTransform: "none",
  minWidth: 160, height: 40,
  display: "flex", alignItems: "center", gap: 1,
  "&:hover":    { backgroundColor: "#1d4ed8" },
  "&:disabled": { backgroundColor: "#93c5fd" },
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const TechnicianTicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // ── Data state ───────────────────────────────────────────────────────────
  const [ticket, setTicket]       = useState(null);
  const [asset, setAsset]         = useState(null);
  const [fetching, setFetching]   = useState(true);
  const [notFound, setNotFound]   = useState(false);

  // ── Form state ───────────────────────────────────────────────────────────
  const [status, setStatus]                   = useState("");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [resolutionCost, setResolutionCost]   = useState("");
  const [invoiceFile, setInvoiceFile]         = useState(null);   

  // ── UI state ─────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState("");

  // ── Close handler ────────────────────────────────────────────────────────
  const handleClose = useCallback((force = false) => {
    if (loading && !force) return;
    navigate("/technician/tickets");
  }, [loading, navigate]);

  // ── Fetch ticket + asset directly from DB ────────────────────────────────
  const loadTicket = useCallback(async () => {
    if (!ticketId) return;
    setFetching(true);
    setError("");
    setNotFound(false);

    try {
      const t = await helpDeskService.fetchById(ticketId);

      if (!t || t.metadata?.deletedFlag) {
        setTicket(null);
        setNotFound(true);
        return;
      }

          setTicket(t);
      setStatus(t.status || "Open");
      setResolutionNotes(t.resolutionNotes || "");
      setResolutionCost(
        t.resolutionCost === 0 || t.resolutionCost
          ? String(t.resolutionCost)
          : "",
      );
      setInvoiceFile(null);

      if (t.assetId && t.companyId) {
        try {
          const companyAssets = await helpDeskService.getCompanyAssets(t.companyId);
          setAsset(companyAssets.find((x) => x.id === t.assetId) || null);
        } catch {
          setAsset(null);
        }
      } else {
        setAsset(null);
      }
    } catch (err) {
      console.error("[TechnicianTicketDetail] Failed to load ticket:", err);
      setError(err.message || "Failed to load ticket.");
    } finally {
      setFetching(false);
    }
  }, [ticketId]);

  useEffect(() => { loadTicket(); }, [loadTicket]);

    // ── Invoice file selection (validates type + size) ───────────────────────
  const handleInvoiceSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";                    // allow re-selecting same file
    if (!file) return;

    try {
      helpDeskService.validateFile(file);   // throws on bad type/size
      setInvoiceFile(file);
      setError("");
    } catch (err) {
      setError(err.message || "Invalid file.");
    }
  };
  // ── Submit handler ───────────────────────────────────────────────────────
  const handleUpdate = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!ticket) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const updateData = {
        status,
        resolutionNotes:
          status === "Resolved" || status === "Closed"
            ? resolutionNotes
            : ticket.resolutionNotes,
      };

      // Auto-claim the ticket if it's unassigned and the technician is updating it
      if (!ticket.assignedToEmail) {
        updateData.assignedToEmail = currentUser?.email || "";
        updateData.assignedToUid = currentUser?.uid || currentUser?.id || "";
      }
      // ── Cost (preserve 0 as a valid value) ──
      if (resolutionCost !== "" && resolutionCost !== null) {
        const parsed = Number(resolutionCost);
        if (!Number.isNaN(parsed)) updateData.resolutionCost = parsed;
      }

      // ── Invoice file → base64 (validated by service) ──
      if (invoiceFile) {
        updateData.maintenanceInvoice = await helpDeskService.convertFileToBase64(
          invoiceFile,
          currentUser?.email || "Technician",
        );
      }

      if (status === "Resolved" || status === "Closed") {
        updateData.resolvedAt = ticket.resolvedAt || new Date().toISOString();
        updateData.resolvedBy = currentUser?.email || "Technician";
      }
      if (status === "Closed") {
        updateData.closedAt = ticket.closedAt || new Date().toISOString();
      }

           await helpDeskService.updateIssue(ticketId, updateData, currentUser);
      await helpDeskService.updateIssue(ticketId, updateData, currentUser);

      setSuccess("Ticket updated successfully!");
      await loadTicket();
      
      // Close the modal after successful update
      handleClose();
    } catch (err) {
      setError(err.message || "Failed to update ticket");
    } finally {
      setLoading(false);
    }
  };

   const statusColor   = STATUS_COLORS[ticket?.status]   || STATUS_COLORS.Open;
  const priorityColor = PRIORITY_COLORS[ticket?.priority || "Medium"];
  const warranty      = getWarrantyStatus(asset?.warrantyExpiry);

   const myEmail = String(currentUser?.email || "").trim().toLowerCase();
  const assigneeEmail = String(ticket?.assignedToEmail || "").trim().toLowerCase();
  
  const myUid = String(currentUser?.uid || currentUser?.id || "").trim();
  const assigneeUid = String(ticket?.assignedToUid || "").trim();

  // If the ticket has NO assignee, any technician can claim/edit it.
  // If it HAS an assignee, they must match by Email OR UID.
  const isAssignee = !ticket?.assignedToEmail || 
    (myEmail && assigneeEmail && myEmail === assigneeEmail) ||
    (myUid && assigneeUid && myUid === assigneeUid);
  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Dialog
      fullScreen
      open={true}
      onClose={handleClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          display: "flex", flexDirection: "column", overflow: "hidden",
          width: "100vw", height: "100vh",
          maxWidth: "100vw", maxHeight: "100vh",
          margin: 0, boxSizing: "border-box",
        },
      }}
    >
      {/* ── APP BAR ──────────────────────────────────────────────────────── */}
      <AppBar sx={{ position: "relative", flexShrink: 0 }} elevation={1}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>

          <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ lineHeight: 1.2 }}>
              {fetching
                ? "Loading ticket…"
                : notFound
                ? "Ticket not found"
                : `Ticket #${(ticket?.ticketId || ticket?.id || "").slice(0, 8)}`}
            </Typography>
            {ticket && (
              <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
                {ticket.issueType} — Raised by {ticket.userEmail || "N/A"}
              </Typography>
            )}
          </Box>

          {ticket && (
            <Stack direction="row" spacing={1} sx={{ mr: 1 }}>
              <Chip
                size="small"
                label={ticket.status}
                sx={{
                  bgcolor: statusColor.bg, color: statusColor.fg,
                  fontWeight: 700, fontSize: "0.72rem",
                }}
              />
              <Chip
                size="small"
                label={ticket.priority || "Medium"}
                sx={{
                  bgcolor: priorityColor.bg, color: priorityColor.fg,
                  fontWeight: 700, fontSize: "0.72rem",
                }}
              />
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      {/* ── BODY ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1, minHeight: 0, overflowY: "auto",
          bgcolor: "#f1f5f9", py: 4,
        }}
      >
        <Container maxWidth="md">

          {/* ── Loading state ───────────────────────────────────────────── */}
          {fetching && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress />
            </Box>
          )}

          {/* ── Not found state ─────────────────────────────────────────── */}
          {!fetching && notFound && (
            <Alert severity="warning">
              Ticket not found or has been deleted.
            </Alert>
          )}

                {!fetching && ticket && (
            <Stack
              spacing={3}
              component="form"
              id="technician-ticket-form"
              onSubmit={handleUpdate}
              noValidate
            >
                          {!isAssignee && ticket.assignedToEmail && (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  You are viewing this ticket in <strong>read-only</strong> mode because
                  it is assigned to <strong>{ticket.assignedToEmail}</strong>.
                  Only the current assignee may update status, cost, invoice, or resolution.
                </Alert>
              )}

              {/* ═══ Ticket Overview ═══════════════════════════════════ */}
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>
                    <InfoOutlinedIcon fontSize="small" />
                    Ticket Overview
                  </SectionTitle>

                  <FormRow>
                    <InfoTile>
                      <FieldLabel>Issue Type</FieldLabel>
                      <FieldValue>{ticket.issueType || "—"}</FieldValue>
                    </InfoTile>

                    <InfoTile>
                      <FieldLabel>Raised By</FieldLabel>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{
                          width: 28, height: 28, fontSize: 13,
                          bgcolor: "#dbeafe", color: "#1e40af", fontWeight: 700,
                        }}>
                          <PersonIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <FieldValue sx={{ fontSize: "0.875rem" }}>
                          {ticket.userEmail || "—"}
                        </FieldValue>
                      </Box>
                    </InfoTile>

                    <InfoTile>
                      <FieldLabel>Created</FieldLabel>
                      <FieldValue sx={{ fontSize: "0.875rem" }}>
                        {formatDate(ticket.metadata?.createdAt || ticket.createdAt)}
                      </FieldValue>
                    </InfoTile>
                  </FormRow>

                  {ticket.resolvedAt && (
                    <FormRow>
                      <InfoTile sx={{ bgcolor: "#f0fdf4", borderColor: "#bbf7d0" }}>
                        <FieldLabel>Resolved At</FieldLabel>
                        <FieldValue sx={{ color: "#166534", fontSize: "0.875rem" }}>
                          {formatDate(ticket.resolvedAt)}
                        </FieldValue>
                        {ticket.resolvedBy && (
                          <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", mt: 0.5 }}>
                            by {ticket.resolvedBy}
                          </Typography>
                        )}
                      </InfoTile>
                    </FormRow>
                  )}
                </CardContent>
              </SectionCard>

              {/* ═══ Description ═══════════════════════════════════════ */}
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>Description</SectionTitle>
                  <Typography
                    sx={{
                      whiteSpace: "pre-wrap", fontSize: 15,
                      color: "text.primary", lineHeight: 1.6,
                    }}
                  >
                    {ticket.description || "No description provided."}
                  </Typography>
                </CardContent>
              </SectionCard>

              {/* ═══ Asset Details ═════════════════════════════════════ */}
              {asset && (
                <SectionCard variant="outlined">
                  <CardContent sx={{ p: 3 }}>
                    <SectionTitle>
                      <InventoryIcon fontSize="small" />
                      Asset Details
                    </SectionTitle>

                    <FormRow>
                      <InfoTile>
                        <FieldLabel>Asset Name</FieldLabel>
                        <FieldValue>{asset.name || asset.assetName || "—"}</FieldValue>
                      </InfoTile>
                      <InfoTile>
                        <FieldLabel>Asset Tag</FieldLabel>
                        <FieldValue sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}>
                          {asset.assetTag || asset.assetId || "—"}
                        </FieldValue>
                      </InfoTile>
                      <InfoTile>
                        <FieldLabel>Location</FieldLabel>
                        <FieldValue sx={{ fontSize: "0.875rem" }}>
                          {asset.locationName || asset.locationCode || "—"}
                        </FieldValue>
                      </InfoTile>
                    </FormRow>

                    <FormRow>
                      <InfoTile sx={{
                        bgcolor: warranty.bg + "33",
                        borderColor: warranty.fg + "55",
                      }}>
                        <FieldLabel>
                          <VerifiedUserIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: "middle" }} />
                          Warranty Expiry
                        </FieldLabel>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                          <FieldValue sx={{ fontSize: "0.875rem" }}>
                            {asset.warrantyExpiry
                              ? new Date(asset.warrantyExpiry).toLocaleDateString("en-IN", {
                                  day: "2-digit", month: "short", year: "numeric",
                                })
                              : "N/A"}
                          </FieldValue>
                          <Chip
                            size="small"
                            label={warranty.label}
                            sx={{
                              bgcolor: warranty.bg,
                              color: warranty.fg,
                              fontWeight: 700,
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                      </InfoTile>

                      {asset.assignedToName && (
                        <InfoTile>
                          <FieldLabel>Current Custodian</FieldLabel>
                          <FieldValue sx={{ fontSize: "0.875rem" }}>
                            {asset.assignedToName}
                          </FieldValue>
                          {asset.assignedDepartmentName && (
                            <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", mt: 0.5 }}>
                              {asset.assignedDepartmentName}
                            </Typography>
                          )}
                        </InfoTile>
                      )}
                    </FormRow>
                  </CardContent>
                </SectionCard>
              )}

              {/* ═══ Existing Resolution Notes ════════════════════════ */}
              {ticket.resolutionNotes && (
                <SectionCard variant="outlined">
                  <CardContent sx={{ p: 3 }}>
                    <SectionTitle>
                      <CheckCircleIcon fontSize="small" sx={{ color: "#16a34a" }} />
                      Existing Resolution Notes
                    </SectionTitle>
                                     <Box sx={{
                      p: 2, borderRadius: 2,
                      bgcolor: "#f0fdf4", border: "1px solid #bbf7d0",
                    }}>
                      <Typography sx={{
                        whiteSpace: "pre-wrap", fontSize: "0.9375rem",
                        color: "#166534", lineHeight: 1.6,
                      }}>
                        {ticket.resolutionNotes}
                      </Typography>

                      {(ticket.resolutionCost !== undefined &&
                        ticket.resolutionCost !== null) && (
                        <Typography
                          sx={{ fontSize: "0.8125rem", color: "#166534", mt: 1 }}
                        >
                          <strong>Cost:</strong> ₹
                          {Number(ticket.resolutionCost).toLocaleString("en-IN")}
                        </Typography>
                      )}

                      {ticket.maintenanceInvoice?.downloadURL && (
                        <Button
                          size="small"
                          startIcon={<ReceiptLongIcon />}
                          onClick={() =>
                            helpDeskService.openInvoiceFile(
                              ticket.maintenanceInvoice.downloadURL,
                            )
                          }
                          sx={{ textTransform: "none", mt: 0.5 }}
                        >
                          {ticket.maintenanceInvoice.fileName || "View invoice"}
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </SectionCard>
              )}

                          {/* ═══ Update Ticket ═════════════════════════════════════ */}
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>
                    <EditNoteIcon fontSize="small" />
                    Update Status
                  </SectionTitle>

                  <FormRow>
                    <FormGroup layoutType="compact">
                      <FieldLabel>New Status</FieldLabel>
                                          <FormControl fullWidth size="small" disabled={!isAssignee}>   <Select
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FormGroup>
                  </FormRow>

                  {(status === "Resolved" || status === "Closed") && (
                    <>
                      <FormRow>
                        <FormGroup layoutType="full">
                          <FieldLabel>
                            Resolution Notes
                            <Typography component="span" sx={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.7rem', textTransform: 'none', marginLeft: '0.25rem' }}>
                              (visible to user)
                            </Typography>
                          </FieldLabel>
                                                 <TextField 
                            multiline 
                            rows={4} 
                            value={resolutionNotes} 
                            onChange={(e) => setResolutionNotes(e.target.value)} 
                            fullWidth 
                            size="small" 
                            placeholder="Enter resolution details..." 
                            // Removed disabled={!isAssignee}
                          />
                        </FormGroup>
                      </FormRow>

                      <FormRow>
                        <FormGroup layoutType="compact">
                          <FieldLabel>Resolution Cost</FieldLabel>
                                                 <TextField 
                            type="number" 
                            value={resolutionCost} 
                            onChange={(e) => setResolutionCost(e.target.value)} 
                            fullWidth 
                            size="small" 
                            placeholder="Enter cost amount" 
                            InputProps={{ startAdornment: (<InputAdornment position="start">₹</InputAdornment>) }} 
                            inputProps={{ min: 0, step: 0.01 }} 
                          
                          />
                        </FormGroup>
                      </FormRow>

                      <FormRow>
                        <FormGroup layoutType="full">
                          <FieldLabel>Upload Maintenance Invoice</FieldLabel>
                          <Box>
                                                      <input 
                              accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls" 
                              style={{ display: 'none' }} 
                              id="technician-invoice-upload" 
                              type="file" 
                              onChange={handleInvoiceSelect} 
                              // Removed disabled={!isAssignee}
                            />
                            <label htmlFor="technician-invoice-upload">
                              <Button variant="outlined" component="span" startIcon={<UploadFileIcon />}>
                                {invoiceFile ? 'Change File' : 'Select Invoice File'}
                              </Button>
                            </label>

                            {invoiceFile && (
                              <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1.5 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <UploadFileIcon fontSize="small" />
                                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                    {invoiceFile.name} ({(invoiceFile.size / 1024).toFixed(2)} KB)
                                  </Typography>
                                                                   <IconButton size="small" onClick={() => setInvoiceFile(null)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Box>
                            )}

                            {/* 
                              Note: I kept the existing ticket invoice display just in case, 
                              but moved the "View" button inline to look cleaner.
                            */}
                            {ticket.maintenanceInvoice?.downloadURL && !invoiceFile && (
                              <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1.5 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <ReceiptLongIcon fontSize="small" />
                                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                    Existing: {ticket.maintenanceInvoice.fileName}
                                  </Typography>
                                  <Button
                                    size="small"
                                    onClick={() => helpDeskService.openInvoiceFile(ticket.maintenanceInvoice.downloadURL)}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    View
                                  </Button>
                                </Stack>
                              </Box>
                            )}

                            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#9ca3af', fontStyle: 'italic' }}>
                              Supported formats: PDF, JPG, PNG, Excel (Max 700KB)
                            </Typography>
                          </Box>
                        </FormGroup>
                      </FormRow>
                    </>
                  )}
                </CardContent>
              </SectionCard>

            </Stack>
          )}
        </Container>
      </Box>

      {/* ── STICKY FOOTER ────────────────────────────────────────────────── */}
      <Box sx={{
        flexShrink: 0, width: "100%", height: 72,
        borderTop: "1px solid #e2e8f0", backgroundColor: "#fff",
        boxShadow: "0 -2px 8px rgba(0,0,0,0.04)",
        display: "flex", alignItems: "center",
        justifyContent: "flex-end", gap: 1.5, px: 3,
      }}>
              <Button onClick={handleClose} disabled={loading} sx={cancelBtnSx}>
          Close
        </Button>
               {ticket && isAssignee && (
          <Button
            type="submit"
            form="technician-ticket-form"
            disabled={loading || fetching}
            sx={submitBtnSx}
          >
            {loading && <CircularProgress size={16} color="inherit" />}
            {loading ? "Saving…" : "Save Update"}
          </Button>
        )}
      </Box>

      {/* ── SUCCESS SNACKBAR ─────────────────────────────────────────────── */}
      <Snackbar
        open={Boolean(success)}
        autoHideDuration={4000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setSuccess("")}
          severity="success"
          variant="filled"
          sx={{ width: "100%", minWidth: 400, fontSize: "0.9rem", fontWeight: 500 }}
        >
          {success}
        </Alert>
      </Snackbar>

      {/* ── ERROR SNACKBAR ───────────────────────────────────────────────── */}
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setError("")}
          severity="error"
          variant="filled"
          icon={<ErrorOutlineIcon />}
          sx={{ width: "100%", minWidth: 400, fontSize: "0.9rem", fontWeight: 500 }}
        >
          {error}
        </Alert>
      </Snackbar>

    </Dialog>
  );
};

export default TechnicianTicketDetail;